import { useEffect } from 'react'

import { useAppForm } from '~/app/pages/HospitalConfiguration/Form/formHooks'
import { useCreateSupportTicketMutation } from '~/hooks/tanstack-query/userSupport/useCreateSupportTicketMutation'
import { useGetSupportHubSpotContact } from '~/hooks/tanstack-query/userSupport/useGetSupportHubSpotContact'

import { SupportView } from '../../UserSupportDialog'
import { formByView } from './formTexts'
import { feedbackFormSchema } from './supportFeedbackFormSchema'
import { useUserSupportMetadata } from './useUserSupportMetadata'

type ActiveView = Exclude<SupportView, 'menu'>

export function useSupportFeedbackForm(activeView: ActiveView, onClose?: () => void) {
    const { data: hubspotContact, isError, isSuccess, isFetched } = useGetSupportHubSpotContact()
    const resolvedHubspotContact = isError ? null : (hubspotContact ?? null)
    const showContactSaveCallout = isFetched && !hubspotContact?.email

    const metadata = useUserSupportMetadata(formByView[activeView].category)
    const createSupportTicket = useCreateSupportTicketMutation({
        successToast: formByView[activeView].successToast,
        onSuccess: onClose,
    })

    const form = useAppForm({
        defaultValues: {
            subject: '',
            description: '',
            email: '',
            phone: '',
        },
        validators: { onChange: feedbackFormSchema },
        onSubmit: async ({ value }) => {
            const parsed = feedbackFormSchema.parse(value)
            await createSupportTicket.mutateAsync({
                subject: parsed.subject,
                description: parsed.description,
                email: parsed.email,
                phone: parsed.phone,
                metadata,
            })
        },
    })

    useEffect(() => {
        if (!isSuccess || !resolvedHubspotContact) {
            return
        }

        if (!form.getFieldValue('email') && resolvedHubspotContact.email) {
            form.setFieldValue('email', resolvedHubspotContact.email)
        }
        if (!form.getFieldValue('phone') && resolvedHubspotContact.phone) {
            form.setFieldValue('phone', resolvedHubspotContact.phone)
        }
    }, [isSuccess, resolvedHubspotContact, form])

    return {
        form,
        metadata,
        showContactSaveCallout,
    }
}
